import { getCookie } from './cookie'

type StoreAccount = {
  merchant_id?: string
  store_name?: string
  account_name?: string
  store_url?: string
  domain?: string
  website?: string
  channel?: string
  email?: string
  create_time?: string | Date
  is_store_marketplace_account?: boolean
}

const normalizeStoreUrl = (raw?: string): string | undefined => {
  if (!raw || typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`
}

export const resolveStoreTrackingFields = (account?: StoreAccount | null) => {
  if (!account) return {}
  const fields: Record<string, string> = {}
  if (account.merchant_id) {
    fields.merchant_id = account.merchant_id
  }
  const storeName = account.store_name || account.account_name
  if (storeName) {
    fields.store_name = storeName
  }
  const storeUrl = normalizeStoreUrl(account.store_url || account.domain || account.website)
  if (storeUrl) {
    fields.store_url = storeUrl
  }
  return fields
}

export const pickPrimaryStoreAccount = (accounts?: StoreAccount[] | null) => {
  if (!Array.isArray(accounts) || !accounts.length) return null
  return accounts.find(account => account.is_store_marketplace_account) || null
}

const normalizeChannel = (channel?: string) => (channel || '').toLowerCase()

const channelsMatch = (accountChannel?: string, targetChannel?: string) => {
  if (!accountChannel || !targetChannel) return false
  return normalizeChannel(accountChannel) === normalizeChannel(targetChannel)
}

const getAccountTimestamp = (account: StoreAccount) => {
  const time = account.create_time
  if (!time) return 0
  return new Date(time).getTime() || 0
}

export const findLinkedStoreAccount = ({
  accounts,
  channel,
  merchantId,
}: {
  accounts?: StoreAccount[]
  channel?: string
  merchantId?: string
}) => {
  const storeAccounts = Array.isArray(accounts)
    ? accounts.filter(account => account.is_store_marketplace_account)
    : []
  if (!storeAccounts.length) return null

  if (merchantId) {
    const byMerchantId = storeAccounts.find(account => account.merchant_id === merchantId)
    if (byMerchantId) return byMerchantId
  }

  if (channel) {
    const byChannel = storeAccounts.filter(account => channelsMatch(account.channel, channel))
    if (byChannel.length === 1) return byChannel[0]
    if (byChannel.length > 1) {
      return byChannel.sort((a, b) => getAccountTimestamp(b) - getAccountTimestamp(a))[0]
    }
  }

  const newest = storeAccounts.sort((a, b) => getAccountTimestamp(b) - getAccountTimestamp(a))[0]
  return newest || pickPrimaryStoreAccount(storeAccounts)
}

export const parseNewLinkedAccountCookie = () => {
  const raw = getCookie('new_linked_account')
  if (!raw) return { merchantId: undefined, channel: undefined }
  try {
    const decoded = decodeURIComponent(raw)
    const [merchantId, channel] = decoded.split('|')
    return { merchantId: merchantId || undefined, channel: channel || undefined }
  } catch (error) {
    console.error('Failed to decode new_linked_account cookie:', error)
    return { merchantId: undefined, channel: undefined }
  }
}

export const resolveStoreAccountFromPayload = (data: unknown) => {
  if (!data) return null
  if (Array.isArray(data)) {
    return findLinkedStoreAccount({ accounts: data }) || data[0] || null
  }
  if (typeof data !== 'object') return null

  const payload = data as {
    storeAccount?: StoreAccount
    accounts?: StoreAccount[]
    channel?: string
    merchantId?: string
  }

  if (payload.storeAccount) return payload.storeAccount

  const accounts = payload.accounts
  if (Array.isArray(accounts) && accounts.length) {
    return findLinkedStoreAccount({
      accounts,
      channel: payload.channel,
      merchantId: payload.merchantId,
    })
  }

  return null
}

const getGaCookies = () => {
  const gaClientId = getCookie('_ga')
  const gaClickId = getCookie('_gcl_aw')
  return {
    ...(gaClientId ? { ga_client_id: gaClientId } : {}),
    ...(gaClickId ? { ga_click_id: gaClickId } : {}),
  }
}

const sendSignUp = (data: unknown) => {
  console.log('sendSignUp: cookie is_sign_up', getCookie('is_sign_up'))
  if (getCookie('is_sign_up')) {
    const account = resolveStoreAccountFromPayload(data)
    const fields = resolveStoreTrackingFields(account)
    return {
      event: 'signup_success',
      email: account?.email || 'N/A',
      merchant_id: fields.merchant_id || account?.merchant_id || 'N/A',
      channel: account?.channel || 'N/A',
      ...(fields.store_name ? { store_name: fields.store_name } : {}),
      ...(fields.store_url ? { store_url: fields.store_url } : {}),
      ...getGaCookies(),
    }
  }
}

const sendOnboardSuccess = (data: unknown) => {
  const account = resolveStoreAccountFromPayload(data)
  const fields = resolveStoreTrackingFields(account)
  return {
    event: 'onboard_success',
    email: account?.email || 'N/A',
    merchant_id: fields.merchant_id || account?.merchant_id || 'N/A',
    channel: account?.channel || 'N/A',
    ...(fields.store_name ? { store_name: fields.store_name } : {}),
    ...(fields.store_url ? { store_url: fields.store_url } : {}),
  }
}

export const sendCcAdded = card => {
  const {
    customer_id, email, brand, exp_month, exp_year, last4
  } = card
  return {
    event: 'cc_added',
    customer_id,
    email,
    brand,
    expiry: `${exp_month}/${exp_year}`,
    last4,
  }
}

export const sendOnboardStep = ({ email, step }) => {
  return {
    event: 'onboard_step_finished',
    email,
    step,
  }
}

const planChange = ({email, oldPlan, newPlan}) => {
  return {
    event: 'plan_changed',
    email,
    old_plan: oldPlan || null,
    new_plan: newPlan,
  }
}

const planCancelled = ({email, oldPlan, cancelReason, cancelReasonSpecified}) => {
  return {
    event: 'plan_cancelled',
    email,
    old_plan: oldPlan || null,
    cancel_reason: cancelReason, 
    cancel_reason_specified: cancelReasonSpecified || '',
  }
}

const pageView = ({ email, page }) => {
  return {
    event: 'pageview',
    email,
    page,
  }
}

const loginFailure = ({ email, message }) => {
  const userId = getCookie('unique_user_id')
  return {
    event: 'login_failure',
    email,
    user_id: userId,
    message,
  }
}

const loginIframe = ({ email, message }) => {
  const userId = getCookie('unique_user_id')
  return {
    event: 'login_iframe',
    email,
    user_id: userId,
    message,
  }
}

// Affiliate Tracking Events
const afftrackPageview = ({ email, userId }) => {
  return {
    event: 'afftrack_pageview',
    afftrack_event: 'pageview',
    email_address: email,
    user_id: userId,
  }
}

const getAfftrackPayload = ({ email, userId, integrationCount, storeCount, gmv, plan, isOrg }) => ({
  email_address: email,
  user_id: userId,
  afftrack_integrations: integrationCount,
  afftrack_stores: storeCount,
  afftrack_gmv: gmv,
  afftrack_plan: plan,
  afftrack_is_org: isOrg,
})

const afftrackLogin = (params) => {
  return {
    event: 'afftrack_login',
    afftrack_event: 'login',
    ...getAfftrackPayload(params),
  }
}

const afftrackInstall = (params) => {
  return {
    event: 'afftrack_install',
    afftrack_event: 'install',
    afftrack_channel: params.channel,
    ...getAfftrackPayload(params),
    ...getGaCookies(),
  }
}

const afftrackIntegrationAdded = (params) => {
  return {
    event: 'afftrack_integration_added',
    afftrack_event: 'integration_added',
    afftrack_channel: params.channel,
    ...getAfftrackPayload(params),
  }
}

const afftrackLogout = ({ email, userId }) => {
  return {
    event: 'afftrack_logout',
    afftrack_event: 'logout',
    email_address: email,
    user_id: userId,
  }
}

const afftrackTrialSignup = ({ email, userId, subscriptionStatus }) => {
  // Only fire for new signups with trialing status (paid plan trial period)
  const isNewSignup = getCookie('is_sign_up')
  const isTrialing = subscriptionStatus === 'trialing'
  
  if (isNewSignup && isTrialing) {
    return {
      event: 'afftrack_trial_signup',
      afftrack_event: 'trial_signup',
      email_address: email,
      user_id: userId,
      ...getGaCookies(),
    }
  }
}

const afftrackOrgCreated = ({ email, userId, orgName, plan }) => {
  return {
    event: 'afftrack_org_created',
    afftrack_event: 'org_created',
    email_address: email,
    user_id: userId,
    org_name: orgName,
    afftrack_plan: plan,
    ...getGaCookies(),
  }
}

const afftrackTrialConversion = ({ email, userId, plan, frequency, amount }) => {
  return {
    event: 'afftrack_trial_conversion',
    afftrack_event: 'trial_conversion',
    email_address: email,
    user_id: userId,
    afftrack_plan: plan,
    afftrack_plan_frequency: frequency,
    afftrack_plan_amount: amount,
  }
}

const afftrackPlanDowngrade = ({ email, userId, plan, frequency, amount }) => {
  return {
    event: 'afftrack_plan_downgrade',
    afftrack_event: 'plan_downgrade',
    email_address: email,
    user_id: userId,
    afftrack_plan: plan,
    afftrack_plan_frequency: frequency,
    afftrack_plan_amount: amount,
  }
}

const afftrackPlanUpgrade = ({ email, userId, plan, frequency, amount }) => {
  return {
    event: 'afftrack_plan_upgrade',
    afftrack_event: 'plan_upgrade',
    email_address: email,
    user_id: userId,
    afftrack_plan: plan,
    afftrack_plan_frequency: frequency,
    afftrack_plan_amount: amount,
  }
}

const afftrackPlanChurn = ({ email, userId, plan, frequency, amount }) => {
  return {
    event: 'afftrack_plan_churn',
    afftrack_event: 'plan_churn',
    email_address: email,
    user_id: userId,
    afftrack_plan: plan,
    afftrack_plan_frequency: frequency,
    afftrack_plan_amount: amount,
  }
}

const afftrackAddonAdd = ({ email, userId, addon, frequency, amount }) => {
  return {
    event: 'afftrack_addon_add',
    afftrack_event: 'addon_add',
    email_address: email,
    user_id: userId,
    afftrack_addon: addon,
    afftrack_addon_plan_frequency: frequency,
    afftrack_addon_plan_amount: amount,
  }
}

const afftrackAddonRemove = ({ email, userId, addon, frequency, amount }) => {
  return {
    event: 'afftrack_addon_remove',
    afftrack_event: 'addon_remove',
    email_address: email,
    user_id: userId,
    afftrack_addon: addon,
    afftrack_addon_plan_frequency: frequency,
    afftrack_addon_plan_amount: amount,
  }
}

const dataFormatterDict = {
  signup_success: sendSignUp,
  cc_added: sendCcAdded,
  onboard_step_finished: sendOnboardStep,
  plan_changed: planChange,
  plan_cancelled: planCancelled,
  pageview: pageView,
  login_failure: loginFailure,
  login_iframe: loginIframe,
  onboard_success: sendOnboardSuccess,
  // Affiliate Tracking Events
  afftrack_pageview: afftrackPageview,
  afftrack_login: afftrackLogin,
  afftrack_install: afftrackInstall,
  afftrack_integration_added: afftrackIntegrationAdded,
  afftrack_logout: afftrackLogout,
  afftrack_trial_signup: afftrackTrialSignup,
  afftrack_org_created: afftrackOrgCreated,
  afftrack_trial_conversion: afftrackTrialConversion,
  afftrack_plan_downgrade: afftrackPlanDowngrade,
  afftrack_plan_upgrade: afftrackPlanUpgrade,
  afftrack_plan_churn: afftrackPlanChurn,
  afftrack_addon_add: afftrackAddonAdd,
  afftrack_addon_remove: afftrackAddonRemove,
}

const pushToDataLayer = (eventData: Record<string, unknown>) => {
  const dataLayer = (window as any).dataLayer || []
  if ((window as any).google_tag_manager) {
    dataLayer.push(eventData)
  }
  else {
    const script = document.getElementById('gtm-script')
    if (script) {
      script.onload = () => dataLayer.push(eventData)
    }
  }
}

export const sendGtmPixel = (data, eventType) => {
  if (!dataFormatterDict[eventType]) {
    console.error('Invalid event type', eventType)
    return
  }
  const eventData = dataFormatterDict[eventType](data)
  // Don't push if formatter returned nothing (e.g., cookie check failed)
  if (!eventData) {
    return
  }
  const storeAccount = resolveStoreAccountFromPayload(data)
  const storeFields = resolveStoreTrackingFields(storeAccount)
  pushToDataLayer({ ...eventData, ...storeFields })
}
